import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardBody, Spinner } from "reactstrap";
import { useMonthlyUsers } from "../../Hooks/useMonthlyUsers";
import { formatNumber } from "../../utils/formatNumber";

const UsersComparisonChart = ({ province, city }) => {

    const {
        categories,
        newUsers,
        activeUsers,
        activation,
        totals,
        isLoading
    } = useMonthlyUsers(3, province, city);

    const options = useMemo(() => ({

        chart: {
            toolbar: {
                show: false
            },
            zoom: {
                enabled: false
            },
            stacked: false,
            fontFamily: "Rubik, sans-serif"
        },

        colors: [
            "#22C55E", // Verde
            "#8B5CF6", // Morado
            "#3B82F6"  // Azul
        ],

        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "45%",
                borderRadius: 4
            }
        },

        stroke: {
            width: [0, 0, 3],
            curve: "smooth",
            dashArray: [0, 0, 8]
        },

        dataLabels: {
            enabled: false
        },

        grid: {
            borderColor: "#F2F4F7",
            strokeDashArray: 4
        },

        markers: {
            size: [0, 0, 4],
            strokeWidth: 0
        },

        xaxis: {

            categories,

            axisBorder: {
                show: false
            },

            axisTicks: {
                show: false
            },

            labels: {
                style: {
                    colors: "#98A2B3"
                }
            }

        },

        // El `seriesName` es lo que amarra cada serie a su eje. NO existe un
        // `yAxisIndex` de serie en ApexCharts (eso es de Highcharts): la
        // librería mapea por el array `yaxis`, y cuando hay más series que ejes
        // reparte las sobrantes sola. Así "Usuarios Activos" terminaba en el
        // eje de retención (tope 100) y su barra se dibujaba contra el techo:
        // 316 activos se veían más altos que 2.000 usuarios nuevos.
        //
        // Los dos conteos comparten el eje izquierdo —misma unidad, así se
        // pueden comparar de un vistazo— y el derecho queda sólo para el
        // porcentaje de retención.
        yaxis: [

            {

                seriesName: ["Nuevos Usuarios", "Usuarios Activos"],

                // Sin max fijo: estaba en 5000 y recortaba la barra apenas la
                // base de usuarios lo superara.
                min: 0,

                tickAmount: 5,

                labels: {
                    formatter: (v) => formatNumber(Math.round(v)),
                    style: {
                        colors: "#98A2B3"
                    }
                }

            },

            {

                seriesName: "Conversión",

                opposite: true,

                min: 0,
                max: 100,

                tickAmount: 4,

                labels: {
                    formatter: (v) => `${v}`,
                    style: {
                        colors: "#98A2B3"
                    }
                }

            }

        ],

        tooltip: {

            shared: true,

            intersect: false,

            // Uno por serie: nuevos, activos (con miles) y conversión (%).
            y: [
                { formatter: formatNumber },
                { formatter: formatNumber },
                { formatter: (v) => `${v}%` }
            ]

        },

        legend: {
            show: false
        }

    }), [categories]);

    // Los `name` no son decorativos: son la llave con la que el array `yaxis`
    // amarra cada serie a su eje. Si se renombra una serie hay que renombrarla
    // también allá.
    const series = useMemo(() => [

        {
            name: "Nuevos Usuarios",
            type: "column",
            data: newUsers
        },

        {
            name: "Usuarios Activos",
            type: "column",
            data: activeUsers
        },

        {
            name: "Conversión",
            type: "line",
            data: activation
        }

    ], [newUsers, activeUsers, activation]);

    // w-100: sin esto la tarjeta no llenaba su columna y dejaba el hueco blanco
    // contra el borde derecho del tablero.
    return (

        <Card className="border-0 shadow-sm w-100">

            <CardBody>

                <div className="d-flex justify-content-between align-items-start mb-3">

                    <div>

                        <h5 className="fw-bold mb-0">
                            Nuevos Usuarios vs Usuarios Activos
                        </h5>

                        <small className="text-muted">
                            (Últimos 3 meses)
                        </small>

                    </div>

                    {totals?.active_growth_percentage !== null &&
                        totals?.active_growth_percentage !== undefined && (
                        <span
                            className="badge"
                            style={{
                                background: totals.active_growth_percentage >= 0
                                    ? "#ECFDF3"
                                    : "#FEF3F2",
                                color: totals.active_growth_percentage >= 0
                                    ? "#12B76A"
                                    : "#F04438",
                                fontWeight: 600,
                                padding: "8px 12px"
                            }}
                        >
                            {totals.active_growth_percentage >= 0 ? "+" : ""}
                            {totals.active_growth_percentage}%
                        </span>
                    )}

                </div>

                {isLoading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <ReactApexChart
                        options={options}
                        series={series}
                        type="line"
                        height={280}
                    />
                )}

                <div className="d-flex justify-content-center gap-4 mt-2">

                    <div className="d-flex align-items-center">

                        <span
                            style={{
                                width: 12,
                                height: 12,
                                background: "#22C55E",
                                borderRadius: 3,
                                marginRight: 8
                            }}
                        />

                        <small>Nuevos Usuarios</small>

                    </div>

                    <div className="d-flex align-items-center">

                        <span
                            style={{
                                width: 12,
                                height: 12,
                                background: "#8B5CF6",
                                borderRadius: 3,
                                marginRight: 8
                            }}
                        />

                        <small>Usuarios Activos</small>

                    </div>

                    <div className="d-flex align-items-center">

                        <span
                            style={{
                                width: 18,
                                height: 3,
                                background: "#3B82F6",
                                borderRadius: 2,
                                marginRight: 8,
                                borderTop: "2px dashed #3B82F6"
                            }}
                        />

                        <small>Conversión</small>

                    </div>

                </div>

                <hr />

                <div className="row text-center">

                    <div className="col">

                        <small className="text-muted">
                            Nuevos Usuarios
                        </small>

                        <h4 className="fw-bold mt-1">
                            {formatNumber(totals?.new_users)}
                        </h4>

                    </div>

                    <div className="col">

                        <small className="text-muted">
                            Usuarios Activos
                        </small>

                        <h4 className="fw-bold mt-1">
                            {formatNumber(totals?.active_users)}
                        </h4>

                    </div>

                    <div className="col">

                        <small className="text-muted">
                            Conversión
                        </small>

                        <h4
                            className="fw-bold mt-1"
                            style={{
                                color: "#12B76A"
                            }}
                        >
                            {totals?.activation_percentage !== null &&
                             totals?.activation_percentage !== undefined
                                ? `${totals.activation_percentage}%`
                                : "—"}
                        </h4>

                    </div>

                </div>

            </CardBody>

        </Card>

    );

};

export default UsersComparisonChart;